import { useCallback, useEffect, useMemo, useState } from 'react'
import TimePicker from 'react-time-picker'
import Tippy from '@tippyjs/react'
import { addMinutes, isAfter, isEqual, parse } from 'date-fns'

import Icon from '../../ui/Icon'

import { toTimeValue } from '../../../utils/dateUtils'

// @ts-ignore
import 'react-time-picker/dist/TimePicker.css'
// @ts-ignore
import 'react-clock/dist/Clock.css'

interface InputTimeProps {
  id?: string
  name: string
  value: string
  required?: boolean
  label?: string
  disabled?: boolean
  startTime?: string
  timeDifference?: string
  minTime?: string
  maxTime?: string
  infoMessage?: string
  errorMessage?: string
  className?: string
  onChange: (name: string, value: string, isValid: boolean) => void
}

export const InputTime = ({
  id,
  name,
  value,
  required = false,
  label,
  disabled = false,
  startTime,
  timeDifference = '30',
  minTime,
  maxTime,
  infoMessage,
  errorMessage,
  className = '',
  onChange,
}: InputTimeProps) => {
  const [timeValue, setTimeValue] = useState(() => toTimeValue(value))
  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  const minimumTime = useMemo(() => {
    const normalizedStartTime = toTimeValue(startTime)
    const safeStartTime = normalizedStartTime || '00:00'
    const parsedDifference = Number.parseInt(timeDifference, 10)
    return addMinutes(
      parse(safeStartTime, 'HH:mm', new Date()),
      Number.isNaN(parsedDifference) ? 0 : parsedDifference
    )
  }, [startTime, timeDifference])

  const validateTime = useCallback(
    (currentValue: string) => {
      const hasValue = currentValue.trim().length > 0

      if (!hasValue) {
        return !required
      }

      if (!startTime) {
        return true
      }

      const inputTime = parse(toTimeValue(currentValue), 'HH:mm', new Date())
      const afterStartTime =
        !startTime ||
        isAfter(inputTime, minimumTime) ||
        isEqual(inputTime, minimumTime)
      const afterMinTime =
        !minTime ||
        inputTime >= parse(toTimeValue(minTime), 'HH:mm', new Date())
      const beforeMaxTime =
        !maxTime ||
        inputTime <= parse(toTimeValue(maxTime), 'HH:mm', new Date())

      return afterStartTime && afterMinTime && beforeMaxTime
    },
    [maxTime, minTime, minimumTime, required, startTime]
  )

  useEffect(() => {
    const normalizedValue = toTimeValue(value)
    setTimeValue(normalizedValue)

    if (!isTouch) {
      return
    }

    setIsError(!validateTime(normalizedValue))
  }, [value, isTouch, validateTime])

  useEffect(() => {
    if (!isTouch) {
      return
    }

    const valid = validateTime(timeValue)
    setIsError(!valid)
    onChange(name, timeValue, valid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimumTime, startTime, timeDifference])

  const handleChange = useCallback(
    (selectedValue: string | null) => {
      setIsTouch(true)

      const normalizedValue = selectedValue
        ? selectedValue
            .split(':')
            .map(part => part.padStart(2, '0'))
            .join(':')
        : ''

      const valid = validateTime(normalizedValue)
      setTimeValue(normalizedValue)
      setIsError(!valid)
      onChange(name, normalizedValue, valid)
    },
    [name, onChange, validateTime]
  )

  const handleBlur = () => {
    setIsFocus(false)
    setIsTouch(true)
    setIsError(!validateTime(timeValue))
  }

  const isRequired = isTouch && required && !timeValue.trim()

  const fieldClass = [
    'field',
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={fieldClass}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className="input-icon-wrap">
        <TimePicker
          id={id}
          name={name}
          value={timeValue || null}
          onChange={handleChange}
          onFocus={() => setIsFocus(true)}
          onBlur={handleBlur}
          disableClock
          format="HH:mm"
          minTime={minTime}
          maxTime={maxTime}
          clearIcon={null}
          clockIcon={null}
          disabled={disabled}
          required={required}
          className="input"
        />
        {infoMessage && (
          <Tippy content={infoMessage} placement="top" className="ph-tooltip">
            <div className="input-icon-toggle">
              <Icon name="info" size={16} />
            </div>
          </Tippy>
        )}
      </div>
      {errorMessage && !isFocus && isError && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>
          {errorMessage}
        </div>
      )}
    </div>
  )
}
